import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import DashboardLayout from '../components/layout/Dashboardlayout.jsx';
import './users.css';
import { gsap } from 'gsap';
import Loader from '../components/layout/preloader';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const pageRef = useRef(null);
  const headerRef = useRef(null);
  const searchBarRef = useRef(null);
  const tableWrapperRef = useRef(null);
  const tableRowsRef = useRef([]);
  const addModalRef = useRef(null);
  const editModalRef = useRef(null);

  const getUsers = async () => {
    const res = await supabase.from('users').select('*').order('created_at', { ascending: false });
    if (res.data) setUsers(res.data);
  };

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' }, delay: 4 });

      tl.fromTo(
        headerRef.current,
        { opacity: 0, y: -24 },
        { opacity: 1, y: 0, duration: 0.65 }
      );

      tl.fromTo(
        searchBarRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.5 },
        '-=0.35'
      );

      tl.fromTo(
        tableWrapperRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.55 },
        '-=0.3'
      );
    }, pageRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    const validRows = tableRowsRef.current.filter(Boolean);
    if (validRows.length === 0) return;
    gsap.fromTo(
      validRows,
      { opacity: 0, x: -18 },
      {
        opacity: 1,
        x: 0,
        duration: 0.38,
        stagger: 0.055,
        ease: 'power2.out',
        delay: 4.15,
      }
    );
  }, [users]);

  const animateModalIn = (ref) => {
    if (!ref.current) return;
    gsap.fromTo(
      ref.current,
      { opacity: 0, scale: 0.94, y: 20 },
      { opacity: 1, scale: 1, y: 0, duration: 0.35, ease: 'power3.out' }
    );
  };

  const animateModalOut = (ref, callback) => {
    if (!ref.current) {
      callback();
      return;
    }
    gsap.to(ref.current, {
      opacity: 0,
      scale: 0.94,
      y: 14,
      duration: 0.22,
      ease: 'power2.in',
      onComplete: callback,
    });
  };

  useEffect(() => {
    if (showAddModal) setTimeout(() => animateModalIn(addModalRef), 10);
  }, [showAddModal]);

  useEffect(() => {
    if (showEditModal) setTimeout(() => animateModalIn(editModalRef), 10);
  }, [showEditModal]);

  const createUser = async () => {
    if (!fullName || !email || !role) return;
    setLoading(true);
    await supabase.from('users').insert([{ full_name: fullName, email, role, status: 'UNVERIFIED' }]);
    setFullName(''); setEmail(''); setRole('');
    setShowAddModal(false);
    await getUsers();
    setLoading(false);
  };

  const updateUser = async (id) => {
    if (!fullName || !email || !role) return;
    setLoading(true);
    await supabase.from('users').update({ full_name: fullName, email, role }).eq('id', id);
    setFullName(''); setEmail(''); setRole('');
    setEditUser(null);
    setShowEditModal(false);
    await getUsers();
    setLoading(false);
  };

  const deleteUser = async (id) => {
    await supabase.from('users').delete().eq('id', id);
    await getUsers();
  };

  const openEditModal = (user) => {
    setEditUser(user);
    setFullName(user.full_name);
    setEmail(user.email);
    setRole(user.role);
    setShowEditModal(true);
  };

  const closeAddModal = () => {
    animateModalOut(addModalRef, () => {
      setShowAddModal(false);
      setFullName(''); setEmail(''); setRole('');
    });
  };

  const closeEditModal = () => {
    animateModalOut(editModalRef, () => {
      setShowEditModal(false);
      setEditUser(null);
      setFullName(''); setEmail(''); setRole('');
    });
  };

  const getInitial = (name) => name ? name.charAt(0).toUpperCase() : '?';

  const filteredUsers = users.filter(u =>
    u.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <>
      <Loader />
      <DashboardLayout activePath="/dashboard/users">
        <div className="users-page" ref={pageRef}>

          <div className="users-header" ref={headerRef}>
            <div className="users-title-block">
              <h1 className="users-title">USER ACCESS MANAGER</h1>
              <p className="users-subtitle">Manage user roles and permissions</p>
            </div>
            <button className="btn-add-user" onClick={() => setShowAddModal(true)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add New User
            </button>
          </div>

          <div className="users-search-bar" ref={searchBarRef}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="search-icon">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="users-table-wrapper" ref={tableWrapperRef}>
            <table className="users-table">
              <thead>
                <tr>
                  <th>AVATAR</th>
                  <th>FULL NAME</th>
                  <th>EMAIL</th>
                  <th>ROLE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user, index) => (
                  <tr key={user.id} ref={(el) => (tableRowsRef.current[index] = el)}>
                    <td>
                      <div className="user-avatar">{getInitial(user.full_name)}</div>
                    </td>
                    <td className="user-fullname">{user.full_name}</td>
                    <td className="user-email">{user.email}</td>
                    <td><span className="role-badge">{user.role}</span></td>
                    <td>
                      <span className={`status-badge ${user.status === 'VERIFIED' ? 'verified' : 'unverified'}`}>
                        {user.status}
                      </span>
                    </td>
                    <td>
                      <div className="action-btns">
                        <button className="action-btn edit-btn" onClick={() => openEditModal(user)} title="Edit user">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                          </svg>
                        </button>
                        <button className="action-btn delete-btn" onClick={() => deleteUser(user.id)} title="Delete user">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                            <path d="M10 11v6M14 11v6" />
                            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {showAddModal && (
            <div className="modal-overlay" onClick={closeAddModal}>
              <div className="modal" ref={addModalRef} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">ADD NEW USER</h2>
                  <button className="modal-close" onClick={closeAddModal}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Full Name <span className="required">*</span></label>
                    <input type="text" placeholder="Enter full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Email Address <span className="required">*</span></label>
                    <input type="email" placeholder="user@thedesignshow.eg" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Role <span className="required">*</span></label>
                    <select value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value="">Select role</option>
                      <option value="Admin">Admin</option>
                      <option value="Receptionist">Receptionist</option>
                      <option value="User">User</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={closeAddModal}>Cancel</button>
                  <button className="btn-confirm" onClick={createUser} disabled={loading}>
                    {loading ? 'Adding...' : 'Add User'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {showEditModal && editUser && (
            <div className="modal-overlay" onClick={closeEditModal}>
              <div className="modal" ref={editModalRef} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2 className="modal-title">EDIT USER</h2>
                  <button className="modal-close" onClick={closeEditModal}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="form-group">
                    <label>Full Name <span className="required">*</span></label>
                    <input type="text" placeholder="Enter full name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Email Address <span className="required">*</span></label>
                    <input type="email" placeholder="user@thedesignshow.eg" value={email} onChange={(e) => setEmail(e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label>Role <span className="required">*</span></label>
                    <select value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value="">Select role</option>
                      <option value="Admin">Admin</option>
                      <option value="Receptionist">Receptionist</option>
                      <option value="User">User</option>
                    </select>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn-cancel" onClick={closeEditModal}>Cancel</button>
                  <button className="btn-confirm" onClick={() => updateUser(editUser.id)} disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </DashboardLayout>
    </>
  );
};

export default Users;